<template>
  <div>
    <!-- Enhanced Hero Section -->
    <section class="hero-enhanced">
      <div
        v-for="(slide, index) in heroSlides"
        :key="slide"
        class="hero-slide"
        :class="{ active: index === currentSlide }"
        :style="{ backgroundImage: `url(${slide})` }"
      ></div>
      <div class="hero-background"></div>
      <div class="container hero-content">
        <div class="hero-text">
          <h1 class="hero-title">
            ยินดีต้อนรับสู่ <span class="hero-title-accent">pruksamarket</span>
          </h1>
          <p class="hero-subtitle">
            ค้นพบสินค้าคุณภาพพรีเมียม ในราคาที่คุ้มค่า<br>
            พร้อมบริการจัดส่งที่รวดเร็วและปลอดภัย
          </p>
          <div class="hero-actions">
            <router-link to="/products" class="btn btn-primary btn-lg hero-btn-primary">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
              เริ่มช้อปปิ้ง
            </router-link>
            <router-link to="/track" class="btn btn-outline btn-lg hero-btn-outline">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              ติดตามคำสั่งซื้อ
            </router-link>
          </div>
        </div>
        <div class="hero-dots" aria-label="สไลด์พื้นหลัง">
          <button
            v-for="(slide, index) in heroSlides"
            :key="`dot-${index}`"
            type="button"
            class="hero-dot"
            :class="{ active: index === currentSlide }"
            :aria-label="`สไลด์ที่ ${index + 1}`"
            @click="goToSlide(index)"
          ></button>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="section features-section">
      <div class="container">
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h3 class="feature-title">สินค้าคุณภาพ</h3>
            <p class="feature-desc">คัดสรรสินค้าคุณภาพสูงจากผู้ผลิตที่เชื่อถือได้</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
            <h3 class="feature-title">จัดส่งรวดเร็ว</h3>
            <p class="feature-desc">จัดส่งสินค้าอย่างรวดเร็วและปลอดภัยถึงมือคุณ</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <h3 class="feature-title">ปลอดภัย 100%</h3>
            <p class="feature-desc">ระบบชำระเงินที่ปลอดภัยและเชื่อถือได้</p>
          </div>
          
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="feature-title">ราคาคุ้มค่า</h3>
            <p class="feature-desc">ราคาที่เป็นธรรมและคุ้มค่ากับคุณภาพ</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Products Section -->
    <section class="section products-section">
      <div class="container">
        <h2 class="section-title">สินค้าแนะนำ</h2>
        <p class="section-subtitle">สินค้าคุณภาพที่ได้รับความนิยมสูงสุด</p>

        <!-- Loading -->
        <div v-if="loading" class="text-center" style="padding: 3rem 0;">
          <div class="loading-spinner" style="margin: 0 auto;"></div>
          <p class="text-muted mt-4">กำลังโหลดสินค้า...</p>
        </div>

        <!-- Products Grid -->
        <div v-else-if="products.length > 0" class="products-grid">
          <ProductCard
            v-for="product in products.slice(0, 8)"
            :key="product.id"
            :product="product"
            @add-to-cart="handleAddToCart"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center" style="padding: 3rem 0;">
          <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="width: 64px; height: 64px; margin: 0 auto 1rem; color: var(--color-text-muted);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-4.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
          </svg>
          <p class="text-muted">ไม่พบสินค้า</p>
        </div>

        <!-- View All Button -->
        <div v-if="products.length > 0" class="text-center mt-8">
          <router-link to="/products" class="btn btn-outline btn-lg">
            ดูสินค้าทั้งหมด
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import api from '../api';
import ProductCard from '../components/ProductCard.vue';
import { useCartStore } from '../stores/cart';
import { useNotification } from '../composables/useNotification';

const products = ref([]);
const loading = ref(true);
const cartStore = useCartStore();
const { showSuccess, showError } = useNotification();

const heroSlides = [
  '/images/bg/51092.jpg',
  '/images/bg/atmosphere.jpg',
  '/images/bg/product-1.jpg'
];
const currentSlide = ref(0);
let slideTimer = null;

const goToSlide = (index) => {
  currentSlide.value = index;
  restartSlideTimer();
};

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % heroSlides.length;
};

const restartSlideTimer = () => {
  if (slideTimer) clearInterval(slideTimer);
  slideTimer = setInterval(nextSlide, 5000);
};

const fetchProducts = async () => {
  try {
    const response = await api.get('/products');
    products.value = response.data.data;
  } catch (error) {
    console.error('Failed to fetch products:', error);
  } finally {
    loading.value = false;
  }
};

const handleAddToCart = async (product) => {
  try {
    await cartStore.addToCart(product.id, null, 1);
    showSuccess(`เพิ่ม "${product.name}" ลงตะกร้าแล้ว`);
  } catch (error) {
    console.error('Add to cart error:', error);
    showError('ไม่สามารถเพิ่มสินค้าได้');
  }
};

onMounted(() => {
  fetchProducts();
  restartSlideTimer();
});

onUnmounted(() => {
  if (slideTimer) clearInterval(slideTimer);
});
</script>

<style scoped>
/* Enhanced Hero Section */
.hero-enhanced {
  position: relative;
  min-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #f5f2eb;
}

.hero-slide {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1.04);
  transition: opacity 1s ease, transform 6s ease;
  z-index: 0;
}

.hero-slide.active {
  opacity: 1;
  transform: scale(1);
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(
      180deg,
      rgba(255, 252, 247, 0.72) 0%,
      rgba(250, 248, 243, 0.68) 45%,
      rgba(245, 242, 235, 0.86) 100%
    ),
    radial-gradient(circle at 20% 50%, rgba(133, 94, 66, 0.06) 0%, transparent 55%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 4rem 1rem 3.5rem;
}

.hero-text {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  line-height: 1.2;
  animation: fadeInUp 0.8s ease-out;
}

.hero-title-accent {
  color: var(--color-primary);
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.125rem;
  color: var(--color-text-light);
  margin-bottom: 2.5rem;
  line-height: 1.8;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.hero-btn-primary {
  box-shadow: 0 4px 12px rgba(133, 94, 66, 0.3);
}

.hero-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(133, 94, 66, 0.4);
}

.hero-btn-outline:hover {
  transform: translateY(-2px);
}

.hero-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.hero-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: rgba(133, 94, 66, 0.3);
  cursor: pointer;
  transition: width 0.25s ease, background 0.25s ease;
}

.hero-dot.active {
  width: 1.4rem;
  background: var(--color-primary);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (min-width: 768px) {
  .hero-enhanced {
    min-height: 640px;
  }
  
  .hero-title {
    font-size: 3.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
}

/* Features Section */
.features-section {
  position: relative;
  /* Gradient แนวตั้งที่ต่างจาก Hero: จาก cream อ่อนไปขาว */
  background: linear-gradient(
    to bottom,
    #faf8f3 0%,
    #fefbf7 30%,
    #ffffff 100%
  );
}

.features-section::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  /* Radial gradient overlay ที่ต่างจาก Hero */
  background: 
    radial-gradient(circle at 70% 30%, rgba(212, 165, 116, 0.04) 0%, transparent 50%),
    radial-gradient(circle at 30% 70%, rgba(133, 94, 66, 0.03) 0%, transparent 50%);
  z-index: 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  position: relative;
  z-index: 1;
}

@media (min-width: 640px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .features-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }
}

.feature-item {
  text-align: center;
  padding: 1.5rem;
  background: var(--color-surface);
  border-radius: 1rem;
  border: 1px solid var(--color-border-light);
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
  border-color: var(--color-primary);
}

.feature-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, var(--color-primary-light), var(--color-primary));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: white;
}

.feature-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.feature-desc {
  font-size: 0.875rem;
  color: var(--color-text-light);
  line-height: 1.6;
}

/* Products Section */
.products-section {
  background: var(--color-surface);
}

</style>
